import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Section, Card, Badge, Button, Alert, Input, Label } from "../design-system";
import { useAuth } from "../auth/AuthProvider";
import { PERM_LABELS, MODULE_LABELS, AREAS, permLabel, moduleLabel } from "./rbacLabels";

const ROLES = ["CUSTOMER", "SALES_OPERATOR", "LOGISTICS_OPERATOR", "ADMIN"];
const ROLE_LABEL = {
  CUSTOMER: "Cliente", SALES_OPERATOR: "Commerciale", LOGISTICS_OPERATOR: "Logistica", ADMIN: "Amministratore",
};
const ROLE_TONE = {
  ADMIN: "success", SALES_OPERATOR: "info", LOGISTICS_OPERATOR: "info", CUSTOMER: "neutral",
};

function fmtDateTime(iso) { return iso ? new Date(iso).toLocaleString("it-IT") : "—"; }

// =========================================================================
// USERS CRUD
// =========================================================================

export function AdminUsersPage() {
  const { apiGet, apiPost, apiPatch } = useAuth();
  const [users, setUsers] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [err, setErr] = useState(null);
  const [notice, setNotice] = useState(null);
  const [filter, setFilter] = useState({ q: "", role: "", active: "true" });
  const [modal, setModal] = useState(null);   // {mode:'create'|'edit', user?}
  const [tempPw, setTempPw] = useState(null); // { email, temp_password }
  // Mar 2026 — per-user pending flag for password-reset / reinvite mutations.
  const [busyUserId, setBusyUserId] = useState(null);
  // Feb 2026 — Multi-select per bulk attiva/disattiva.
  // Reset password bulk NON è supportato per motivi di sicurezza.
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkStatus, setBulkStatus] = useState(null);

  const load = async () => {
    setErr(null);
    try {
      const qp = new URLSearchParams();
      if (filter.q) qp.set("q", filter.q);
      if (filter.role) qp.set("role", filter.role);
      if (filter.active !== "") qp.set("active", filter.active);
      const [u, c] = await Promise.all([
        apiGet(`/api/admin/users?${qp.toString()}`),
        apiGet("/api/customers"),
      ]);
      setUsers(u); setCustomers(c);
    } catch (e) { setErr(e?.message || String(e)); setUsers([]); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const toggle = async (u) => {
    try {
      await apiPost(`/api/admin/users/${u.id}/toggle-active`, {});
      setNotice(`${u.email} → ${u.is_active ? "disattivato" : "attivato"}`);
      await load();
    } catch (e) { setErr(e?.data?.detail || e?.message || String(e)); }
  };

  const resetPw = async (u) => {
    if (busyUserId) return;
    if (!window.confirm(`Generare una nuova password temporanea per ${u.email}?`)) return;
    setBusyUserId(u.id);
    try {
      const r = await apiPost(`/api/admin/users/${u.id}/reset-password`, {});
      setTempPw({ email: u.email, temp_password: r.temp_password });
    } catch (e) { setErr(e?.data?.detail || e?.message || String(e)); }
    finally { setBusyUserId(null); }
  };

  // v34.8/v34.24 — Reinvia invito email (single-source per "Reimposta accesso" e
  // "Reinvia invito"). Chiama l'endpoint canonico /send-invite che invalida
  // eventuali token precedenti (single-active-invite invariant).
  const reinvite = async (u) => {
    if (busyUserId) return;
    if (!window.confirm(`Inviare un nuovo link di accesso a ${u.email}?`)) return;
    setBusyUserId(u.id);
    try {
      await apiPost(`/api/admin/users/${u.id}/send-invite`, {});
      setNotice(`Link di accesso inviato a ${u.email}`);
      setTimeout(() => setNotice(null), 3000);
    } catch (e) { setErr(e?.data?.detail || e?.message || String(e)); }
    finally { setBusyUserId(null); }
  };

  const toggleRow = (id) => {
    const s = new Set(selectedIds);
    if (s.has(id)) s.delete(id); else s.add(id);
    setSelectedIds(s);
  };
  const toggleAllUsers = () => {
    if (!users) return;
    if (selectedIds.size === users.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(users.map((u) => u.id)));
  };
  // Bulk attiva/disattiva: N chiamate al singolo toggle-active (Feature Freeze,
  // no nuovo endpoint dedicato). Utenti totali ~50-100 → ok.
  const doBulkToggle = async (targetActive) => {
    setBulkStatus(null);
    const targets = (users || []).filter(
      (u) => selectedIds.has(u.id) && u.is_active !== targetActive
    );
    if (targets.length === 0) {
      setBulkStatus(`ℹ️ Nessun utente da ${targetActive ? "attivare" : "disattivare"} nella selezione.`);
      return;
    }
    if (!targetActive && !window.confirm(
      `Disattivare ${targets.length} utenti selezionati? Non potranno più accedere al portale.`)) {
      return;
    }
    let ok = 0, ko = 0;
    for (const u of targets) {
      try {
        await apiPost(`/api/admin/users/${u.id}/toggle-active`, {});
        ok++;
      } catch { ko++; }
    }
    setBulkStatus(`✅ ${ok} utenti ${targetActive ? "attivati" : "disattivati"}${ko > 0 ? ` · ${ko} errori` : ""}`);
    setSelectedIds(new Set());
    await load();
  };

  return (
    <Section title="Utenti" subtitle="Gestione utenti operativi"
             data-testid="admin-users-page"
             actions={<Button variant="primary" onClick={() => setModal({ mode: "create" })}
                              data-testid="users-new-btn">+ Nuovo utente</Button>}>
      {tempPw && (
        <Alert tone="warning" data-testid="temp-password-alert">
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <div>
              <strong>Credenziali generate per {tempPw.email}</strong>
            </div>
            <div>
              Password temporanea: {" "}
              <code data-testid="temp-password-value"
                    style={{ background: "#fff", padding: "2px 6px",
                             fontFamily: "monospace", fontSize: ".95rem" }}>
                {tempPw.temp_password}
              </code>
            </div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              <Button size="sm"
                      onClick={() => { navigator.clipboard.writeText(tempPw.temp_password); setNotice("Password copiata"); }}
                      data-testid="temp-password-copy">Copia password</Button>
              <Button size="sm"
                      onClick={() => {
                        const txt = `Portale GE SURGELATI\nUtente: ${tempPw.email}\nPassword temporanea: ${tempPw.temp_password}\nAl primo accesso dovrai cambiarla.`;
                        navigator.clipboard.writeText(txt);
                        setNotice("Credenziali copiate");
                      }}
                      data-testid="temp-password-copy-full">Copia credenziali</Button>
              <Button size="sm" onClick={() => setTempPw(null)}>Chiudi</Button>
            </div>
            <small style={{ color: "#78350F" }}>
              Comunica queste credenziali via canale sicuro. L'utente dovrà cambiare la password al primo accesso.
            </small>
          </div>
        </Alert>
      )}
      {notice && <Alert tone="success">{notice}</Alert>}
      {err && <Alert variant="danger">Errore: {typeof err === "string" ? err : JSON.stringify(err)}</Alert>}

      <Card>
        <Card.Header>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end" }}>
            <div style={{ minWidth: 220 }}>
              <Label>Cerca</Label>
              <Input value={filter.q} onChange={(e) => setFilter({ ...filter, q: e.target.value })}
                     data-testid="users-search" placeholder="email o nome" />
            </div>
            <div>
              <Label>Ruolo</Label>
              <select className="ds-input" value={filter.role} data-testid="users-role-filter"
                      onChange={(e) => setFilter({ ...filter, role: e.target.value })}>
                <option value="">Tutti</option>
                {ROLES.map((r) => <option key={r} value={r}>{ROLE_LABEL[r]}</option>)}
              </select>
            </div>
            <div>
              <Label>Stato</Label>
              <select className="ds-input" value={filter.active} data-testid="users-active-filter"
                      onChange={(e) => setFilter({ ...filter, active: e.target.value })}>
                <option value="true">Solo attivi</option>
                <option value="false">Solo disattivati</option>
                <option value="">Mostra tutti</option>
              </select>
            </div>
            <Button onClick={load} variant="primary" data-testid="users-apply">Applica</Button>
          </div>
        </Card.Header>
        <Card.Body>
          {!users && <p className="muted">Caricamento…</p>}
          {users && users.length === 0 && <Alert tone="info">Nessun utente trovato.</Alert>}
          {users && users.length > 0 && (
            <div className="ds-table-wrap">
              <table className="ds-table" data-testid="users-table">
                <thead><tr>
                  <th style={{ width: 30 }}>
                    <input type="checkbox"
                           data-testid="users-select-all"
                           checked={selectedIds.size === users.length && users.length > 0}
                           onChange={toggleAllUsers} />
                  </th>
                  <th>Email</th><th>Nome</th><th>Ruolo</th><th>Cliente</th>
                  <th>Stato</th><th>Ultimo accesso</th><th>Azioni</th>
                </tr></thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.id} data-testid={`user-row-${u.email}`}>
                      <td>
                        <input type="checkbox"
                               data-testid={`user-select-${u.email}`}
                               checked={selectedIds.has(u.id)}
                               onChange={() => toggleRow(u.id)} />
                      </td>
                      <td><code style={{ fontSize: ".9rem" }}>{u.email}</code></td>
                      <td>{u.full_name || "—"}</td>
                      <td>{u.roles[0] ? <Badge tone={ROLE_TONE[u.roles[0]]}>{ROLE_LABEL[u.roles[0]] || u.roles[0]}</Badge> : "—"}</td>
                      <td className="muted" style={{ fontSize: ".85rem" }}>
                        {u.customer_id
                          ? (u.customer_code && u.customer_business_name
                              ? <><code>{u.customer_code}</code> — {u.customer_business_name}</>
                              : "—")
                          : "—"}
                      </td>
                      <td>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                          <Badge tone={u.is_active ? "success" : "neutral"}>
                            {u.is_active ? "Attivo" : "Disattivato"}
                          </Badge>
                          {u.is_locked && <Badge tone="danger">Bloccato</Badge>}
                          {u.must_change_password && <Badge tone="warning">Cambio password richiesto</Badge>}
                          {u.roles[0] === "CUSTOMER" && u.customer_profile_verified === false && (
                            <Badge tone="warning">Profilo da verificare</Badge>
                          )}
                        </div>
                      </td>
                      <td className="muted" style={{ fontSize: ".85rem" }}>{fmtDateTime(u.last_login_at)}</td>
                      <td>
                        <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                          <Button size="sm" onClick={() => setModal({ mode: "edit", user: u })}
                                  data-testid={`user-edit-${u.email}`}>Modifica</Button>
                          <Button size="sm" variant={u.is_active ? "danger" : "primary"}
                                  onClick={() => toggle(u)}
                                  data-testid={`user-toggle-${u.email}`}>
                            {u.is_active ? "Disattiva" : "Riattiva"}
                          </Button>
                          <Button size="sm" onClick={() => reinvite(u)}
                                  loading={busyUserId === u.id}
                                  disabled={busyUserId === u.id}
                                  data-testid={`user-invite-${u.email}`}>Invia link di accesso</Button>
                          <Button size="sm" variant="outline" onClick={() => resetPw(u)}
                                  loading={busyUserId === u.id}
                                  disabled={busyUserId === u.id}
                                  data-testid={`user-reset-${u.email}`}>Genera password temporanea</Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {bulkStatus && (
                <div data-testid="users-bulk-status"
                     style={{ marginTop: 8, padding: "8px 12px", borderRadius: 8,
                              background: bulkStatus.startsWith("❌") ? "#fee2e2" : "#dcfce7",
                              border: `1px solid ${bulkStatus.startsWith("❌") ? "#fca5a5" : "#86efac"}` }}>
                  {bulkStatus}
                  <button style={{ marginLeft: 12, background: "transparent", border: "none",
                                    cursor: "pointer" }}
                          onClick={() => setBulkStatus(null)}>✕</button>
                </div>
              )}
              {selectedIds.size > 0 && (
                <div data-testid="users-bulk-bar"
                     style={{ position: "sticky", bottom: 0, background: "#fef3c7", border: "1px solid #fbbf24",
                              padding: "8px 12px", borderRadius: 8, marginTop: 8,
                              display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                  <strong>{selectedIds.size} utenti selezionati</strong>
                  <span>·</span>
                  <Button variant="primary" data-testid="users-bulk-activate"
                          onClick={() => doBulkToggle(true)}>Attiva</Button>
                  <Button data-testid="users-bulk-deactivate"
                          onClick={() => doBulkToggle(false)}>Disattiva</Button>
                  <Button data-testid="users-bulk-clear"
                          onClick={() => setSelectedIds(new Set())}>Deseleziona</Button>
                </div>
              )}
            </div>
          )}
        </Card.Body>
      </Card>

      {modal && (
        <UserFormModal
          mode={modal.mode}
          user={modal.user}
          onClose={() => setModal(null)}
          onSubmit={async (payload) => {
            try {
              if (modal.mode === "create") {
                const r = await apiPost("/api/admin/users", payload);
                if (r.temp_password) setTempPw({ email: r.email, temp_password: r.temp_password });
                setNotice("Utente creato");
              } else {
                await apiPatch(`/api/admin/users/${modal.user.id}`, payload);
                setNotice("Utente aggiornato");
              }
              setModal(null);
              await load();
            } catch (e) { setErr(e?.data?.detail || e?.message || String(e)); }
          }}
        />
      )}
    </Section>
  );
}

function UserFormModal({ mode, user, onClose, onSubmit }) {
  const { apiGet, apiPost } = useAuth();
  const [form, setForm] = useState({
    email: user?.email || "",
    full_name: user?.full_name || "",
    phone: user?.phone || "",
    role: user?.roles?.[0] || "SALES_OPERATOR",
    customer_id: user?.customer_id || "",
    is_active: user?.is_active ?? true,
    password: "",
  });
  const [busy, setBusy] = useState(false);
  // v34.8 — credential mode: "invite" (default) or "manual"
  const [credMode, setCredMode] = useState("invite");
  const pwMode = credMode; // legacy alias for existing render logic
  // Customer typeahead
  const [custQ, setCustQ] = useState("");
  const [custResults, setCustResults] = useState([]);
  const [custPicked, setCustPicked] = useState(
    user?.customer_id ? { id: user.customer_id, code: user.customer_code, business_name: user.customer_business_name } : null
  );
  const [custLoading, setCustLoading] = useState(false);
  // Mini-modal create customer
  const [showCreateCust, setShowCreateCust] = useState(false);

  // Debounced search on custQ change
  useEffect(() => {
    if (form.role !== "CUSTOMER") return;
    if (!custQ || custQ.length < 2) { setCustResults([]); return; }
    const t = setTimeout(async () => {
      setCustLoading(true);
      try {
        const r = await apiGet(`/api/customers?q=${encodeURIComponent(custQ)}`);
        setCustResults((r || []).slice(0, 8));
      } catch { setCustResults([]); }
      setCustLoading(false);
    }, 250);
    return () => clearTimeout(t);
  }, [custQ, form.role, apiGet]);

  const pickCustomer = (c) => {
    setCustPicked(c);
    setForm((f) => ({ ...f, customer_id: c.id }));
    setCustQ("");
    setCustResults([]);
  };
  const clearCustomer = () => {
    setCustPicked(null);
    setForm((f) => ({ ...f, customer_id: "" }));
  };
  const onCustomerCreated = (c) => {
    setShowCreateCust(false);
    pickCustomer(c);
  };

  const submit = async () => {
    setBusy(true);
    const payload = { ...form };
    if (payload.customer_id === "") payload.customer_id = null;
    payload.credential_mode = credMode;
    if (credMode === "invite") { delete payload.password; }
    if (!payload.password) delete payload.password;
    if (mode === "edit") delete payload.email; // Immutable
    if (mode === "edit") delete payload.password;
    if (mode === "edit") delete payload.credential_mode;
    await onSubmit(payload);
    setBusy(false);
  };

  const showCustomerBlock = form.role === "CUSTOMER";
  const canSubmit = form.email && form.role &&
    (!showCustomerBlock || !!form.customer_id);

  return (
    <div role="dialog" data-testid="user-modal"
         style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.45)",
                  display: "flex", alignItems: "center", justifyContent: "center", zIndex: 60 }}>
      <div style={{ background: "#fff", borderRadius: 12, width: "95%", maxWidth: 620,
                    padding: 20, maxHeight: "90vh", overflowY: "auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h2 style={{ margin: 0 }}>{mode === "create" ? "Nuovo utente" : `Modifica ${user.email}`}</h2>
          <Button onClick={onClose}>✕</Button>
        </div>

        <div style={{ display: "grid", gap: 12, marginTop: 16 }}>
          {/* Tipo utente (role) */}
          <div>
            <Label>Tipo utente *</Label>
            <select className="ds-input" value={form.role} data-testid="user-role"
                    onChange={(e) => { setForm({ ...form, role: e.target.value, customer_id: "" }); clearCustomer(); }}
                    disabled={mode === "edit"}>
              {ROLES.map((r) => <option key={r} value={r}>{ROLE_LABEL[r]}</option>)}
            </select>
            {mode === "edit" && <small className="muted">Il tipo utente non è modificabile qui.</small>}
          </div>

          {/* Cliente collegato (solo CUSTOMER) */}
          {showCustomerBlock && (
            <div style={{ padding: 12, background: "#F8FAFC", borderRadius: 8, border: "1px solid #E2E8F0" }}>
              <Label>Cliente collegato *</Label>
              {custPicked ? (
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between",
                              padding: "6px 10px", background: "#fff", border: "1px solid #CBD5E1", borderRadius: 6 }}
                     data-testid="user-customer-picked">
                  <span><code>{custPicked.code}</code> — <strong>{custPicked.business_name}</strong></span>
                  <Button size="sm" onClick={clearCustomer} data-testid="user-customer-clear">Cambia</Button>
                </div>
              ) : (
                <>
                  <Input value={custQ} data-testid="user-customer-search"
                         placeholder="Cerca per codice cliente o ragione sociale…"
                         onChange={(e) => setCustQ(e.target.value)} />
                  {custLoading && <small className="muted">Ricerca…</small>}
                  {custResults.length > 0 && (
                    <div style={{ maxHeight: 220, overflowY: "auto", border: "1px solid #CBD5E1",
                                  borderRadius: 6, marginTop: 4 }}
                         data-testid="user-customer-results">
                      {custResults.map((c) => (
                        <button key={c.id}
                                data-testid={`user-customer-result-${c.code}`}
                                onClick={() => pickCustomer(c)}
                                style={{ display: "block", width: "100%", textAlign: "left",
                                         padding: "6px 10px", background: "#fff", border: "none",
                                         borderBottom: "1px solid #F1F5F9", cursor: "pointer" }}>
                          <code>{c.code}</code> — {c.business_name}
                        </button>
                      ))}
                    </div>
                  )}
                  <div style={{ marginTop: 8 }}>
                    <Button size="sm" onClick={() => setShowCreateCust(true)}
                            data-testid="user-customer-new-btn">+ Nuovo cliente</Button>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Dati accesso */}
          {mode === "create" && (
            <div>
              <Label>Email *</Label>
              <Input type="email" value={form.email} data-testid="user-email"
                     onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </div>
          )}
          <div>
            <Label>Nome completo</Label>
            <Input value={form.full_name} data-testid="user-fullname"
                   onChange={(e) => setForm({ ...form, full_name: e.target.value })} />
          </div>
          <div>
            <Label>Telefono</Label>
            <Input value={form.phone} data-testid="user-phone"
                   onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          </div>

          {/* Credenziali (solo create) */}
          {mode === "create" && (
            <div style={{ padding: 12, background: "#F8FAFC", borderRadius: 8, border: "1px solid #E2E8F0" }}>
              <Label>Come vuoi dare accesso all'utente?</Label>
              <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 8, fontSize: ".88rem" }}>
                <label style={{ display: "inline-flex", gap: 6, alignItems: "center", cursor: "pointer" }}>
                  <input type="radio" checked={credMode === "invite"} onChange={() => setCredMode("invite")}
                         data-testid="user-cred-invite" />
                  <span><strong>Invia invito via email</strong> — consigliato</span>
                </label>
                <label style={{ display: "inline-flex", gap: 6, alignItems: "center", cursor: "pointer" }}>
                  <input type="radio" checked={credMode === "manual"} onChange={() => setCredMode("manual")}
                         data-testid="user-cred-manual" />
                  <span>Genera credenziali temporanee</span>
                </label>
              </div>
              {credMode === "manual" && (
                <Input type="text" value={form.password} data-testid="user-password"
                       placeholder="Password iniziale (lascia vuoto per generarla automaticamente)"
                       onChange={(e) => setForm({ ...form, password: e.target.value })} />
              )}
              <small className="muted" style={{ display: "block", marginTop: 4 }}>
                {credMode === "invite"
                  ? "L'utente riceverà un'email con un link personale (48h, uso singolo) per impostare la propria password."
                  : "Verrà mostrata una password temporanea da comunicare all'utente. Sarà obbligato a cambiarla al primo accesso."}
              </small>
            </div>
          )}
        </div>

        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 20 }}>
          <Button onClick={onClose}>Annulla</Button>
          <Button variant="primary" onClick={submit} loading={busy} disabled={busy || !canSubmit}
                  data-testid="user-submit">
            {busy ? "Attendi…" : mode === "create" ? "Crea utente" : "Salva"}
          </Button>
        </div>
      </div>

      {showCreateCust && (
        <CustomerCreateMiniModal
          onClose={() => setShowCreateCust(false)}
          onCreated={onCustomerCreated}
        />
      )}
    </div>
  );
}

// ---- Mini-modal: crea un nuovo Customer inline durante l'onboarding utente ----
function CustomerCreateMiniModal({ onClose, onCreated }) {
  const { apiGet, apiPost } = useAuth();
  const [f, setF] = useState({
    code: "", business_name: "", vat_number: "", tax_code: "",
    email: "", phone: "",
    legal_address: "", legal_zip_code: "", legal_city: "", legal_province: "",
    customer_category_id: "", agent_id: "",
  });
  const [cats, setCats] = useState([]);
  const [agents, setAgents] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    (async () => {
      try { setCats(await apiGet("/api/admin/customer-categories")); } catch { setCats([]); }
      try { setAgents(await apiGet("/api/admin/agents?active=true")); } catch { setAgents([]); }
    })();
  }, [apiGet]);

  const submit = async () => {
    setErr(null);
    if (!f.code.trim()) return setErr("Il codice cliente è obbligatorio.");
    if (!f.business_name.trim()) return setErr("La ragione sociale è obbligatoria.");
    setBusy(true);
    try {
      const payload = { ...f };
      Object.keys(payload).forEach((k) => { if (payload[k] === "") payload[k] = null; });
      const created = await apiPost("/api/admin/customers", payload);
      onCreated(created);
    } catch (e) {
      const d = e?.data?.detail;
      const msg = d === "customer_code_already_exists" ? "Codice cliente già esistente."
        : d === "vat_number_already_exists" ? "Partita IVA già registrata."
        : d || e?.message || "Errore durante la creazione.";
      setErr(msg);
    } finally { setBusy(false); }
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.6)",
                  display: "flex", alignItems: "center", justifyContent: "center", zIndex: 70 }}
         data-testid="customer-create-modal">
      <div style={{ background: "#fff", borderRadius: 12, width: "95%", maxWidth: 640,
                    padding: 20, maxHeight: "90vh", overflowY: "auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h3 style={{ margin: 0 }}>Nuovo cliente</h3>
          <Button onClick={onClose}>✕</Button>
        </div>
        {err && <Alert variant="danger" style={{ marginTop: 12 }}>{err}</Alert>}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 12 }}>
          <div><Label>Codice cliente *</Label>
            <Input value={f.code} data-testid="ncust-code" onChange={(e) => setF({ ...f, code: e.target.value })} placeholder="es. 0001500" /></div>
          <div><Label>Ragione sociale *</Label>
            <Input value={f.business_name} data-testid="ncust-name" onChange={(e) => setF({ ...f, business_name: e.target.value })} /></div>
          <div><Label>Partita IVA</Label>
            <Input value={f.vat_number} data-testid="ncust-vat" onChange={(e) => setF({ ...f, vat_number: e.target.value })} /></div>
          <div><Label>Codice fiscale</Label>
            <Input value={f.tax_code} data-testid="ncust-tax" onChange={(e) => setF({ ...f, tax_code: e.target.value })} /></div>
          <div><Label>Email</Label>
            <Input value={f.email} data-testid="ncust-email" onChange={(e) => setF({ ...f, email: e.target.value })} /></div>
          <div><Label>Telefono</Label>
            <Input value={f.phone} data-testid="ncust-phone" onChange={(e) => setF({ ...f, phone: e.target.value })} /></div>
          <div style={{ gridColumn: "1/-1" }}><Label>Indirizzo</Label>
            <Input value={f.legal_address} data-testid="ncust-addr" onChange={(e) => setF({ ...f, legal_address: e.target.value })} /></div>
          <div><Label>CAP</Label>
            <Input value={f.legal_zip_code} data-testid="ncust-zip" onChange={(e) => setF({ ...f, legal_zip_code: e.target.value })} maxLength={10} /></div>
          <div><Label>Città</Label>
            <Input value={f.legal_city} data-testid="ncust-city" onChange={(e) => setF({ ...f, legal_city: e.target.value })} /></div>
          <div><Label>Provincia</Label>
            <Input value={f.legal_province} data-testid="ncust-prov" onChange={(e) => setF({ ...f, legal_province: e.target.value.toUpperCase() })} maxLength={4} /></div>
          <div><Label>Categoria cliente</Label>
            <select className="ds-input" value={f.customer_category_id} data-testid="ncust-cat"
                    onChange={(e) => setF({ ...f, customer_category_id: e.target.value })}>
              <option value="">— Nessuna —</option>
              {cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select></div>
          <div><Label>Agente</Label>
            <select className="ds-input" value={f.agent_id} data-testid="ncust-agent"
                    onChange={(e) => setF({ ...f, agent_id: e.target.value })}>
              <option value="">— Nessuno —</option>
              {agents.map((a) => <option key={a.id} value={a.id}>{a.code} — {a.display_name || a.first_name + " " + (a.last_name || "")}</option>)}
            </select></div>
        </div>
        <p className="muted" style={{ fontSize: ".8rem", marginTop: 10 }}>
          Il cliente verrà creato come <strong>non verificato</strong>. Il primo utente Cliente collegato dovrà confermare i dati anagrafici prima di poter creare ordini.
        </p>
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 14 }}>
          <Button onClick={onClose}>Annulla</Button>
          <Button variant="primary" onClick={submit} loading={busy} disabled={busy} data-testid="ncust-submit">
            {busy ? "Creazione…" : "Crea cliente"}
          </Button>
        </div>
      </div>
    </div>
  );
}

// =========================================================================
// RBAC MATRIX
// =========================================================================

export function AdminRbacPage() {
  const { apiGet, apiPost } = useAuth();
  const [matrix, setMatrix] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState({});
  const [notice, setNotice] = useState(null);
  const [view, setView] = useState("sintetica"); // "sintetica" | "avanzata"
  const [q, setQ] = useState("");
  const [moduleFilter, setModuleFilter] = useState("");

  const load = async () => {
    setErr(null);
    try { setMatrix(await apiGet("/api/rbac/matrix")); }
    catch (e) { setErr(e?.message || String(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const toggle = async (roleKey, permKey, granted) => {
    const key = `${roleKey}:${permKey}`;
    setBusy((b) => ({ ...b, [key]: true }));
    try {
      await apiPost("/api/rbac/toggle", {
        role_key: roleKey, permission_key: permKey, granted,
      });
      setNotice("Permesso aggiornato");
      setTimeout(() => setNotice(null), 2500);
      await load();
    } catch (e) {
      setErr(e?.data?.detail || e?.message || String(e));
    } finally {
      setBusy((b) => ({ ...b, [key]: false }));
    }
  };

  // ---------- Derived indices ----------
  const roleByKey = useMemo(() => {
    if (!matrix) return {};
    return Object.fromEntries(matrix.roles.map((r) => [r.key, r]));
  }, [matrix]);
  const permByKey = useMemo(() => {
    if (!matrix) return {};
    return Object.fromEntries(matrix.permissions.map((p) => [p.key, p]));
  }, [matrix]);
  const isGranted = (roleKey, permKey) => {
    const r = roleByKey[roleKey]; const p = permByKey[permKey];
    if (!r || !p || !matrix) return false;
    return !!matrix.matrix[`${r.id}:${p.id}`];
  };
  const perRoleCounts = useMemo(() => {
    if (!matrix) return {};
    const out = { CUSTOMER: 0, SALES_OPERATOR: 0, LOGISTICS_OPERATOR: 0 };
    for (const key of Object.keys(matrix.matrix)) {
      const [rid] = key.split(":");
      const r = matrix.roles.find((x) => x.id === rid);
      if (r && out[r.key] !== undefined) out[r.key] += 1;
    }
    return out;
  }, [matrix]);

  if (!matrix) {
    return (
      <Section title="Ruoli & Permessi" data-testid="admin-rbac-page">
        <Card><Card.Body>Caricamento…</Card.Body></Card>
      </Section>
    );
  }

  const totalPerms = matrix.permissions.length;

  // Predicate: permission is togglable for an operator role (SALES/LOGISTICS)?
  const isOperatorTogglable = (p) => !p.admin_only && !p.key.endsWith(".own");

  // ---------- Sintetica: build one card per operator role ----------
  const buildRoleAreas = (roleKey) => {
    return AREAS.map((area) => {
      const perms = area.keys
        .map((k) => permByKey[k])
        .filter((p) => p && isOperatorTogglable(p));
      return { ...area, perms };
    }).filter((area) => area.perms.length > 0);
  };
  const salesAreas = buildRoleAreas("SALES_OPERATOR");
  const logisticsAreas = buildRoleAreas("LOGISTICS_OPERATOR");

  // ---------- Avanzata: build unified table data ----------
  const advancedRows = matrix.permissions
    .filter((p) => isOperatorTogglable(p))
    .filter((p) => !moduleFilter || p.module === moduleFilter)
    .filter((p) => {
      if (!q) return true;
      const needle = q.toLowerCase();
      return (
        p.key.toLowerCase().includes(needle) ||
        (permLabel(p.key) || "").toLowerCase().includes(needle) ||
        (moduleLabel(p.module) || "").toLowerCase().includes(needle)
      );
    })
    .sort((a, b) => {
      const ma = moduleLabel(a.module), mb = moduleLabel(b.module);
      if (ma !== mb) return ma.localeCompare(mb);
      return permLabel(a.key).localeCompare(permLabel(b.key));
    });
  // Group advanced rows by module for collapsible sections
  const advancedByModule = advancedRows.reduce((acc, p) => {
    (acc[p.module] = acc[p.module] || []).push(p);
    return acc;
  }, {});
  const advancedModules = Object.keys(advancedByModule).sort((a, b) =>
    moduleLabel(a).localeCompare(moduleLabel(b))
  );

  const adminOnlyPerms = matrix.permissions
    .filter((p) => p.admin_only)
    .sort((a, b) => permLabel(a.key).localeCompare(permLabel(b.key)));

  // ---------- Render helpers ----------
  const renderToggleCell = (roleKey, p) => {
    const granted = isGranted(roleKey, p.key);
    const key = `${roleKey}:${p.key}`;
    const isBusy = !!busy[key];
    return (
      <label style={{ display: "inline-flex", alignItems: "center", gap: 6, cursor: isBusy ? "wait" : "pointer" }}>
        <input
          type="checkbox"
          data-testid={`rbac-cb-${roleKey}-${p.key}`}
          checked={granted}
          disabled={isBusy}
          onChange={(e) => toggle(roleKey, p.key, e.target.checked)}
        />
      </label>
    );
  };

  const RoleCard = ({ roleKey, title, subtitle, testid, areas }) => (
    <Card data-testid={testid}>
      <Card.Header>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div>
            <strong>{title}</strong>
            <div className="muted" style={{ fontSize: ".82rem" }}>{subtitle}</div>
          </div>
          <Badge tone="info">{perRoleCounts[roleKey] || 0} permessi attivi</Badge>
        </div>
      </Card.Header>
      <Card.Body style={{ padding: 0 }}>
        {areas.map((area) => (
          <details key={area.key} open style={{ borderTop: "1px solid #E2E8F0" }}>
            <summary style={{ padding: "8px 12px", fontWeight: 600, fontSize: ".9rem", cursor: "pointer", background: "#F8FAFC" }}>
              {area.label} <span className="muted" style={{ fontWeight: 400 }}>· {area.perms.length}</span>
            </summary>
            <div style={{ padding: "6px 12px 10px" }}>
              {area.perms.map((p) => (
                <div key={p.key}
                     data-testid={`rbac-sintetica-row-${roleKey}-${p.key}`}
                     style={{ display: "flex", alignItems: "center", justifyContent: "space-between",
                              padding: "4px 0", borderBottom: "1px dashed #F1F5F9" }}>
                  <span style={{ fontSize: ".88rem" }}>{permLabel(p.key)}</span>
                  {renderToggleCell(roleKey, p)}
                </div>
              ))}
            </div>
          </details>
        ))}
      </Card.Body>
    </Card>
  );

  return (
    <Section title="Ruoli & Permessi"
             subtitle="Gestisci cosa possono fare gli operatori commerciali e logistici nel portale."
             data-testid="admin-rbac-page">
      {notice && <Alert tone="success" data-testid="rbac-notice">{notice}</Alert>}
      {err && <Alert variant="danger" data-testid="rbac-error">Errore: {typeof err === "string" ? err : JSON.stringify(err)}</Alert>}

      {/* Summary chip */}
      <div className="muted" style={{ fontSize: ".85rem", marginBottom: 12 }} data-testid="rbac-summary">
        {matrix.roles.length} ruoli · {totalPerms} permessi · modifiche registrate
      </div>

      {/* ADMIN + CUSTOMER info boxes */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
        <Card data-testid="rbac-admin-box">
          <Card.Body>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <strong>Amministratore</strong>
              <Badge tone="success">Accesso completo</Badge>
            </div>
            <p className="muted" style={{ fontSize: ".88rem", margin: "6px 0 0" }}>
              Accesso completo a tutte le funzioni del portale. Non modificabile.
            </p>
          </Card.Body>
        </Card>
        <Card data-testid="rbac-customer-box">
          <Card.Body>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <strong>Cliente</strong>
              <Badge tone="neutral">Permessi protetti</Badge>
            </div>
            <p className="muted" style={{ fontSize: ".88rem", margin: "6px 0 0" }}>
              Può accedere esclusivamente alle funzioni self-service e ai propri dati.
              {" "}{perRoleCounts.CUSTOMER || 0} permessi di sistema, non modificabili.
            </p>
          </Card.Body>
        </Card>
      </div>

      {/* View toggle */}
      <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
        <Button variant={view === "sintetica" ? "primary" : "secondary"}
                onClick={() => setView("sintetica")}
                data-testid="rbac-view-sintetica">Vista sintetica</Button>
        <Button variant={view === "avanzata" ? "primary" : "secondary"}
                onClick={() => setView("avanzata")}
                data-testid="rbac-view-avanzata">Vista avanzata</Button>
      </div>

      {/* ================= SINTETICA ================= */}
      {view === "sintetica" && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}
             data-testid="rbac-sintetica-container">
          <RoleCard roleKey="SALES_OPERATOR"
                    title="Operatore Commerciale"
                    subtitle="Vendite, ordini, listini, contratti."
                    testid="rbac-role-sales"
                    areas={salesAreas} />
          <RoleCard roleKey="LOGISTICS_OPERATOR"
                    title="Operatore Logistico"
                    subtitle="Spedizioni, piani di carico, DDT, consegne."
                    testid="rbac-role-logistics"
                    areas={logisticsAreas} />
        </div>
      )}

      {/* ================= AVANZATA ================= */}
      {view === "avanzata" && (
        <div data-testid="rbac-avanzata-container">
          {/* Search + module filter */}
          <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
            <Input placeholder="Cerca permesso…" value={q}
                   onChange={(e) => setQ(e.target.value)}
                   data-testid="rbac-search"
                   style={{ maxWidth: 280 }} />
            <select value={moduleFilter}
                    onChange={(e) => setModuleFilter(e.target.value)}
                    data-testid="rbac-module-filter"
                    className="ds-input" style={{ maxWidth: 240 }}>
              <option value="">Tutti i moduli</option>
              {[...new Set(matrix.permissions.filter(isOperatorTogglable).map((p) => p.module))]
                .sort((a, b) => moduleLabel(a).localeCompare(moduleLabel(b)))
                .map((m) => <option key={m} value={m}>{moduleLabel(m)}</option>)}
            </select>
            <div className="muted" style={{ alignSelf: "center", fontSize: ".85rem" }}>
              {advancedRows.length} permessi
            </div>
          </div>

          {advancedModules.map((mod) => (
            <details key={mod} open style={{ marginBottom: 8 }}>
              <summary style={{ cursor: "pointer", fontWeight: 700, padding: "8px 10px",
                                background: "#F1F5F9", borderRadius: 4 }}>
                {moduleLabel(mod)} <span className="muted" style={{ fontWeight: 400 }}>· {advancedByModule[mod].length}</span>
              </summary>
              <div className="ds-table-wrap" style={{ marginTop: 6 }}>
                <table className="ds-table" data-testid={`rbac-avanzata-table-${mod}`}>
                  <thead>
                    <tr>
                      <th style={{ width: "60%" }}>Permesso</th>
                      <th style={{ textAlign: "center" }}>Commerciale</th>
                      <th style={{ textAlign: "center" }}>Logistico</th>
                    </tr>
                  </thead>
                  <tbody>
                    {advancedByModule[mod].map((p) => (
                      <tr key={p.key} data-testid={`rbac-avanzata-row-${p.key}`}>
                        <td>
                          <div style={{ fontSize: ".9rem" }}>{permLabel(p.key)}</div>
                          <code style={{ fontSize: ".72rem", color: "#64748b" }}>{p.key}</code>
                        </td>
                        <td style={{ textAlign: "center" }}>{renderToggleCell("SALES_OPERATOR", p)}</td>
                        <td style={{ textAlign: "center" }}>{renderToggleCell("LOGISTICS_OPERATOR", p)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </details>
          ))}

          {/* Admin-only section */}
          {adminOnlyPerms.length > 0 && (
            <details style={{ marginTop: 16 }} data-testid="rbac-admin-only-section">
              <summary style={{ cursor: "pointer", fontWeight: 700, padding: "8px 10px",
                                background: "#FEF3C7", borderRadius: 4 }}>
                Funzioni riservate all'Amministratore <span className="muted" style={{ fontWeight: 400 }}>· {adminOnlyPerms.length}</span>
              </summary>
              <p className="muted" style={{ fontSize: ".85rem", padding: "6px 10px" }}>
                Questi permessi sono protetti dal sistema e non possono essere concessi a Commerciale o Logistico.
              </p>
              <div className="ds-table-wrap">
                <table className="ds-table">
                  <thead>
                    <tr><th>Permesso</th><th>Modulo</th></tr>
                  </thead>
                  <tbody>
                    {adminOnlyPerms.map((p) => (
                      <tr key={p.key}>
                        <td>
                          <div style={{ fontSize: ".9rem" }}>{permLabel(p.key)}</div>
                          <code style={{ fontSize: ".72rem", color: "#64748b" }}>{p.key}</code>
                        </td>
                        <td>{moduleLabel(p.module)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </details>
          )}
        </div>
      )}
    </Section>
  );
}

// =========================================================================
// PRICING SIMULATOR
// =========================================================================

export function AdminPricingSimulatorPage() {
  const { apiGet, apiPost } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [deliveryPoints, setDeliveryPoints] = useState([]);
  const [form, setForm] = useState({
    customer_id: "",
    delivery_mode: "DELIVERY",
    delivery_point_id: "",
    requested_delivery_date: "",
    advance_payment: false,
    ignore_stacking: false,
    lines: [{ product_id: "", qty_cartons: 1 }],
  });
  const [result, setResult] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [c, p] = await Promise.all([apiGet("/api/customers"), apiGet("/api/products")]);
        setCustomers(c); setProducts(p);
      } catch (e) { setErr(e?.message || String(e)); }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      if (!form.customer_id) { setDeliveryPoints([]); return; }
      try {
        const dps = await apiGet(`/api/delivery-points`);
        const own = dps.filter((d) => d.customer_id === form.customer_id);
        setDeliveryPoints(own);
        if (own.length > 0 && !form.delivery_point_id) {
          const def = own.find((d) => d.is_default) || own[0];
          setForm((f) => ({ ...f, delivery_point_id: def.id }));
        }
      } catch (e) { /* silent */ }
    })();
    // eslint-disable-next-line
  }, [form.customer_id]);

  const addLine = () => setForm({ ...form, lines: [...form.lines, { product_id: "", qty_cartons: 1 }] });
  const rmLine = (i) => setForm({ ...form, lines: form.lines.filter((_, j) => j !== i) });
  const setLine = (i, patch) => setForm({
    ...form, lines: form.lines.map((l, j) => (j === i ? { ...l, ...patch } : l)),
  });

  const simulate = async () => {
    setErr(null); setBusy(true);
    try {
      const payload = {
        customer_id: form.customer_id,
        delivery_mode: form.delivery_mode,
        delivery_point_id: form.delivery_mode === "DELIVERY" ? form.delivery_point_id || null : null,
        requested_delivery_date: form.requested_delivery_date || null,
        advance_payment: form.advance_payment,
        ignore_stacking: form.ignore_stacking,
        lines: form.lines.filter((l) => l.product_id && l.qty_cartons > 0),
      };
      const r = await apiPost("/api/pricing/simulate", payload);
      setResult(r);
    } catch (e) { setErr(e?.data?.detail || e?.message || String(e)); }
    finally { setBusy(false); }
  };

  const productById = Object.fromEntries(products.map((p) => [p.id, p]));

  return (
    <Section title="Pricing Simulator" subtitle="Read-only — utilizza il pricing service identico all'ordine reale"
             data-testid="admin-pricing-simulator">
      {err && <Alert variant="danger">Errore: {typeof err === "string" ? err : JSON.stringify(err)}</Alert>}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
        {/* INPUT */}
        <Card>
          <Card.Header><strong>Input</strong></Card.Header>
          <Card.Body>
            <div style={{ display: "grid", gap: 12 }}>
              <div>
                <Label>Cliente *</Label>
                <select className="ds-input" value={form.customer_id} data-testid="ps-customer"
                        onChange={(e) => setForm({ ...form, customer_id: e.target.value, delivery_point_id: "" })}>
                  <option value="">— Seleziona —</option>
                  {customers.map((c) => <option key={c.id} value={c.id}>{c.business_name}</option>)}
                </select>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <div>
                  <Label>Modalità</Label>
                  <select className="ds-input" value={form.delivery_mode} data-testid="ps-mode"
                          onChange={(e) => setForm({ ...form, delivery_mode: e.target.value })}>
                    <option value="DELIVERY">Consegna</option>
                    <option value="PICKUP">Ritiro</option>
                  </select>
                </div>
                {form.delivery_mode === "DELIVERY" && (
                  <div>
                    <Label>Delivery Point *</Label>
                    <select className="ds-input" value={form.delivery_point_id} data-testid="ps-dp"
                            onChange={(e) => setForm({ ...form, delivery_point_id: e.target.value })}>
                      <option value="">— Seleziona —</option>
                      {deliveryPoints.map((dp) => (
                        <option key={dp.id} value={dp.id}>{dp.name} ({dp.city}, {dp.region})</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <div>
                  <Label>Data richiesta consegna</Label>
                  <Input type="date" value={form.requested_delivery_date} data-testid="ps-date"
                         onChange={(e) => setForm({ ...form, requested_delivery_date: e.target.value })} />
                </div>
                <div style={{ display: "flex", flexDirection: "column", justifyContent: "flex-end", gap: 4 }}>
                  <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <input type="checkbox" checked={form.advance_payment} data-testid="ps-advance"
                           onChange={(e) => setForm({ ...form, advance_payment: e.target.checked })} />
                    Pagamento anticipato
                  </label>
                  <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <input type="checkbox" checked={form.ignore_stacking} data-testid="ps-nostack"
                           onChange={(e) => setForm({ ...form, ignore_stacking: e.target.checked })} />
                    Ignora stacking rules
                  </label>
                </div>
              </div>

              <div>
                <Label>Righe prodotto</Label>
                {form.lines.map((l, i) => (
                  <div key={i} style={{ display: "grid", gridTemplateColumns: "3fr 1fr auto", gap: 6, marginBottom: 6 }}>
                    <select className="ds-input" value={l.product_id} data-testid={`ps-line-product-${i}`}
                            onChange={(e) => setLine(i, { product_id: e.target.value })}>
                      <option value="">— Prodotto —</option>
                      {products.map((p) => <option key={p.id} value={p.id}>{p.sku} · {p.name}</option>)}
                    </select>
                    <Input type="number" min="1" value={l.qty_cartons} data-testid={`ps-line-qty-${i}`}
                           onChange={(e) => setLine(i, { qty_cartons: parseInt(e.target.value || "0", 10) })} />
                    <Button size="sm" variant="danger" onClick={() => rmLine(i)}
                            disabled={form.lines.length === 1}>✕</Button>
                  </div>
                ))}
                <Button size="sm" onClick={addLine} data-testid="ps-add-line">+ Riga</Button>
              </div>

              <Button variant="primary" onClick={simulate} loading={busy} disabled={busy || !form.customer_id}
                      data-testid="ps-simulate-btn">
                {busy ? "Calcolo…" : "Simula pricing"}
              </Button>
            </div>
          </Card.Body>
        </Card>

        {/* OUTPUT */}
        <Card>
          <Card.Header><strong>Risultato</strong></Card.Header>
          <Card.Body data-testid="ps-result">
            {!result && <p className="muted">Compila l'input e clicca "Simula pricing".</p>}
            {result && (
              <div style={{ display: "grid", gap: 8 }}>
                <div><strong>Subtotale:</strong> € {result.subtotal}</div>
                <div><strong>Sconti totali:</strong> −€ {result.discount_total}</div>
                <div><strong>Trasporto:</strong> € {result.shipping_cost}</div>
                <div style={{ fontSize: "1.4rem", fontWeight: 700 }}>
                  Totale: € {result.total}
                </div>
                <div className="muted" style={{ fontSize: ".85rem" }}>
                  Pallet: <strong>{result.total_pallets || result.pallet_count || "—"}</strong> ·{" "}
                  Kg totali: <strong>{result.total_weight_kg}</strong> ·{" "}
                  Contributo PFA previsto: <strong>{result.pfa_potential_kg || "—"} kg</strong>
                </div>
                {result.pricing_result_hash && (
                  <div className="muted" style={{ fontSize: ".8rem" }}>
                    Hash: <code>{result.pricing_result_hash.slice(0, 16)}…</code>
                  </div>
                )}

                {result.applied_discounts?.length > 0 && (
                  <div>
                    <Label>Sconti applicati</Label>
                    <ul style={{ margin: 0, paddingLeft: 20 }}>
                      {result.applied_discounts.map((a, i) => (
                        <li key={i} data-testid={`ps-discount-${a.type || i}`}>
                          <strong>{a.type}</strong>: −€ {a.amount}
                          {a.rule && <span className="muted"> · {a.rule}</span>}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {result.skipped_discounts?.length > 0 && (
                  <div>
                    <Label>Sconti scartati (non cumulabili / saltati)</Label>
                    <ul style={{ margin: 0, paddingLeft: 20, color: "#888" }}>
                      {result.skipped_discounts.map((s, i) => (
                        <li key={i}><em>{s.type}</em>: {s.reason || s.rule}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {result.lines?.length > 0 && (
                  <details>
                    <summary className="muted" style={{ cursor: "pointer" }}>Righe pricing dettagliate</summary>
                    <div className="ds-table-wrap" style={{ marginTop: 8 }}>
                      <table className="ds-table">
                        <thead><tr>
                          <th>SKU</th><th>Q.tà</th><th>€ base</th><th>€ effettivo</th><th>Kg PFA</th>
                        </tr></thead>
                        <tbody>
                          {result.lines.map((l, i) => {
                            const prod = productById[l.product_id];
                            return (
                              <tr key={i}>
                                <td>{prod?.sku || l.product_id.slice(0, 8)}</td>
                                <td>{l.qty_cartons}</td>
                                <td>€ {l.unit_price_base}</td>
                                <td>€ {l.unit_price_effective}</td>
                                <td>{l.pfa_contribution_kg}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </details>
                )}

                {result.explanation?.length > 0 && (
                  <details>
                    <summary className="muted" style={{ cursor: "pointer" }}>Spiegazione step-by-step</summary>
                    <ul style={{ margin: 0, paddingLeft: 20 }}>
                      {result.explanation.map((e, i) => <li key={i} style={{ fontSize: ".9rem" }}>{typeof e === "string" ? e : JSON.stringify(e)}</li>)}
                    </ul>
                  </details>
                )}
              </div>
            )}
          </Card.Body>
        </Card>
      </div>
    </Section>
  );
}

// =========================================================================
// LATE LOAD PLANS
// =========================================================================

export function AdminLateLoadPlansPage() {
  const { apiGet } = useAuth();
  const [rows, setRows] = useState(null);
  const [carriers, setCarriers] = useState([]);
  const [err, setErr] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [plans, cs] = await Promise.all([
          apiGet("/api/logistics/load-plans?late=true"),
          apiGet("/api/carriers"),
        ]);
        setRows(plans); setCarriers(cs);
      } catch (e) { setErr(e?.message || String(e)); setRows([]); }
    })();
  }, []);

  const carrierById = Object.fromEntries(carriers.map((c) => [c.id, c]));

  const withDaysLate = useMemo(() => {
    if (!rows) return [];
    const today = new Date();
    return rows.map((p) => {
      const ld = new Date(p.loading_date);
      const days = Math.floor((today - ld) / (86400000));
      return { ...p, days_late: days };
    }).sort((a, b) => b.days_late - a.days_late);
  }, [rows]);

  return (
    <Section title="Piani Carichi in ritardo"
             subtitle="loading_date < oggi AND status ∈ (DRAFT, INVIATO)"
             data-testid="admin-late-plans-page">
      {err && <Alert variant="danger">Errore: {err}</Alert>}
      {!rows && <p className="muted">Caricamento…</p>}
      {rows && rows.length === 0 && <Alert tone="success">Nessun piano in ritardo. 🎉</Alert>}
      {rows && rows.length > 0 && (
        <Card>
          <Card.Body>
            <div className="ds-table-wrap">
              <table className="ds-table" data-testid="late-plans-table">
                <thead><tr>
                  <th>Vettore</th><th>Data carico</th><th>Giorni ritardo</th>
                  <th>Stato</th><th>Rev.</th><th></th>
                </tr></thead>
                <tbody>
                  {withDaysLate.map((p) => (
                    <tr key={p.id} data-testid={`late-row-${p.id}`}>
                      <td>{carrierById[p.carrier_id]?.business_name || p.carrier_id?.slice(0, 8)}</td>
                      <td>{new Date(p.loading_date).toLocaleDateString("it-IT")}</td>
                      <td><Badge tone={p.days_late >= 3 ? "danger" : "warning"}>{p.days_late} gg</Badge></td>
                      <td>{p.status}{p.has_unsent_changes && " *"}</td>
                      <td>{p.revision_number}</td>
                      <td>
                        <Link to={`/logistics/load-plans/${p.id}`}>
                          <Button size="sm">Apri</Button>
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card.Body>
        </Card>
      )}
    </Section>
  );
}
